/**
 * Firm Settings › Integrations: every outside service ClientFlow uses, with its status and what to do
 * next; each person's Google connections; client QuickBooks companies; the tracked lead links; where the
 * client list comes from; and the firm's own QuickBooks. Never shows a key, host, user name, account id
 * or connection string: the API doesn't send any.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckCircle2, CircleAlert, CircleDashed, ExternalLink, FileSpreadsheet, RefreshCw } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { Link, useSearch } from "wouter";
import { ask } from "@/components/dialogs";
import { Empty, ListSearch, Loading, Pager, Section, Segmented, StatRow, useServerPaged } from "@/components/page";
import { Button } from "@/components/ui/button";
import { api, errorMessage } from "@/lib/api";
import { formatCents } from "@clientflow/shared";
import { formatDate, formatDateTime, plural } from "@/lib/format";
import type { Me } from "@/lib/session";
import { toast } from "@/lib/use-toast";
import { cn } from "@/lib/utils";
import { CopyButton, LeadChannelLinks, type FullSetup } from "@/pages/staff/leads";
import { TableScroll } from "@/components/table-scroll";

/** The project's docs, where each service is set up (the repository on GitHub, main branch). */
export const DOCS_BASE = "https://github.com/angoupou-spec/c-a-clientflow-back-up/blob/main/docs/";
const docUrl = (doc: { file: string; anchor: string }) => `${DOCS_BASE}${doc.file}${doc.anchor ? `#${doc.anchor}` : ""}`;

type Fact = { label: string; value: string; kind?: "text" | "date" | "datetime"; tone?: "good" | "warn" | "bad" | "neutral" };
type Card = {
  key: string;
  name: string;
  status: "connected" | "not_set_up" | "needs_attention";
  summary: string;
  facts: Fact[];
  lastWorkedAt: string | null;
  lastError: { at: string | null; message: string } | null;
  doc: { file: string; anchor: string; label: string };
  links: Array<{ label: string; href: string }>;
  addresses?: { note: string; items: Array<{ label: string; value: string }> };
  test: { service: string; last: { ok: boolean; message: string; at: string } | null } | null;
};

const badge = "inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium";
const TONES = {
  green: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300",
  amber: "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300",
  red: "bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300",
  grey: "bg-secondary text-secondary-foreground",
  blue: "bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300",
} as const;

export function Pill({ tone, children }: { tone: keyof typeof TONES; children: ReactNode }) {
  return <span className={cn(badge, TONES[tone])}>{children}</span>;
}

const STATUS: Record<Card["status"], { label: string; tone: keyof typeof TONES }> = {
  connected: { label: "Connected", tone: "green" },
  needs_attention: { label: "Needs attention", tone: "red" },
  not_set_up: { label: "Not set up", tone: "grey" },
};

const factTone: Record<NonNullable<Fact["tone"]>, string> = {
  good: "text-emerald-700 dark:text-emerald-400",
  warn: "text-amber-700 dark:text-amber-400",
  bad: "text-red-700 dark:text-red-400",
  neutral: "",
};

function factValue(f: Fact) {
  if (f.kind === "datetime") return formatDateTime(f.value);
  if (f.kind === "date") return formatDate(f.value);
  return f.value;
}

/** A row of a long address, with Copy: shown in full, wrapping on a phone. */
function AddressRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid gap-1 sm:grid-cols-[9.5rem_minmax(0,1fr)_auto] sm:items-center sm:gap-2">
      <span className="text-xs text-muted-foreground">{label}</span>
      <code className="min-w-0 break-all rounded bg-muted px-2 py-1 text-xs">{value}</code>
      <span>
        <CopyButton text={value} label={`Copy ${label}`} />
      </span>
    </div>
  );
}

function ServiceCard({ c, wide }: { c: Card; wide?: boolean }) {
  const [last, setLast] = useState(c.test?.last ?? null);
  const test = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send<{ ok: boolean; message: string; at: string }>("POST", `/api/firm/integrations/test/${c.test!.service}`),
    onSuccess: (r) => setLast(r),
  });
  const st = STATUS[c.status];
  const Icon = c.status === "connected" ? CheckCircle2 : c.status === "needs_attention" ? CircleAlert : CircleDashed;
  return (
    <section aria-labelledby={`svc-${c.key}`} className={cn("flex min-w-0 flex-col overflow-hidden rounded-lg border border-card-border bg-card", wide && "lg:col-span-2")}>
      <div className="flex items-start justify-between gap-3 border-b border-border bg-secondary/50 px-5 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <Icon className={cn("h-4 w-4 shrink-0", c.status === "connected" ? "text-emerald-600 dark:text-emerald-400" : c.status === "needs_attention" ? "text-red-600 dark:text-red-400" : "text-muted-foreground")} aria-hidden />
          <h2 id={`svc-${c.key}`} className="min-w-0 text-[15px] font-semibold">
            {c.name}
          </h2>
        </div>
        <Pill tone={st.tone}>{st.label}</Pill>
      </div>
      <div className="flex flex-1 flex-col gap-4 p-5 text-sm">
        <p>{c.summary}</p>
        <dl className={cn("grid gap-x-6 gap-y-1.5", wide ? "sm:grid-cols-2" : "")}>
          {c.facts.map((f) => (
            <div key={f.label} className="flex min-w-0 items-baseline justify-between gap-3 border-b border-dashed border-border pb-1">
              <dt className="min-w-0 text-muted-foreground">{f.label}</dt>
              <dd className={cn("tabular min-w-0 max-w-[60%] break-words text-right font-medium", f.tone && factTone[f.tone])}>{factValue(f)}</dd>
            </div>
          ))}
          <div className="flex min-w-0 items-baseline justify-between gap-3 border-b border-dashed border-border pb-1">
            <dt className="text-muted-foreground">Last worked</dt>
            <dd className="tabular shrink-0 text-right font-medium">{c.lastWorkedAt ? formatDateTime(c.lastWorkedAt) : "Not yet"}</dd>
          </div>
        </dl>
        {c.lastError && (
          <div role="status" className="rounded-md border border-red-300 bg-red-50 px-3 py-2 dark:border-red-900 dark:bg-red-950/40">
            <p className="font-medium">Last problem{c.lastError.at ? `, ${formatDateTime(c.lastError.at)}` : ""}</p>
            <p className="text-muted-foreground">{c.lastError.message}</p>
          </div>
        )}
        {c.addresses && (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">{c.addresses.note}</p>
            {c.addresses.items.map((a) => (
              <AddressRow key={a.label} label={a.label} value={a.value} />
            ))}
          </div>
        )}
        {last && (
          <p className={cn("rounded-md border px-3 py-2", last.ok ? "border-emerald-300 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/40" : "border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40")}>
            <span className="font-medium">Last test, {formatDateTime(last.at)}: </span>
            {last.message}
          </p>
        )}
        {test.error && <p className="text-destructive">{errorMessage(test.error)}</p>}
        <div className="mt-auto flex flex-wrap items-center gap-2 pt-1">
          {c.test && (
            <Button size="sm" variant="outline" disabled={test.isPending} onClick={() => test.mutate()}>
              <RefreshCw className={cn(test.isPending && "animate-spin")} aria-hidden /> {test.isPending ? "Testing…" : "Test connection"}
            </Button>
          )}
          <a href={docUrl(c.doc)} target="_blank" rel="noreferrer" className="inline-flex min-h-8 items-center gap-1.5 rounded-md border border-border px-3 text-xs font-medium hover:bg-muted">
            Set up: {c.doc.label} <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          </a>
          {c.links.map((l) => (
            <Link key={l.href} href={l.href} className="inline-flex min-h-8 items-center rounded-md px-2 text-xs font-medium text-primary hover:underline">
              {l.label}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/** The order cards are laid out in: QuickBooks across the page, then pairs of about the same length. */
const ORDER = ["quickbooks", "stripe", "email", "sms", "clerk", "ai", "connector", "google", "thumbtack", "backups", "database"];

/** Firm Settings › Integrations › Services (owners and admins). */
export function IntegrationServices() {
  const { data, isLoading, error } = useQuery<{ cards: Card[]; isDemo: boolean }>({ queryKey: ["/api/firm/integrations/services"] });
  if (isLoading) return <Loading tall />;
  if (error || !data) return <Empty>{error ? errorMessage(error) : "Not available."}</Empty>;
  const cards = [...data.cards].sort((a, b) => ORDER.indexOf(a.key) - ORDER.indexOf(b.key));
  const counts = { connected: 0, needs_attention: 0, not_set_up: 0 };
  for (const c of cards) counts[c.status]++;
  return (
    <div className="space-y-6">
      <StatRow
        stats={[
          { label: "Connected", value: counts.connected, tone: "success" },
          { label: "Needs attention", value: counts.needs_attention, tone: counts.needs_attention ? "danger" : "default" },
          { label: "Not set up", value: counts.not_set_up },
          { label: "Services", value: cards.length },
        ]}
      />
      <p className="text-sm text-muted-foreground">
        Keys and passwords stay in Replit Secrets: this page shows only whether each one is set, when the service last worked, and the last problem in plain words. A test makes a read-only call (an email test goes only to you).
        {data.isDemo && " In the demo firm, the services are this server's own; the demo never sends email or texts, and its QuickBooks companies are built-in samples."}
      </p>
      <div className="grid gap-4 lg:grid-cols-2">
        {cards.map((c) => (
          <ServiceCard key={c.key} c={c} wide={c.key === "quickbooks"} />
        ))}
      </div>
    </div>
  );
}

// --- 2. Staff connections ---------------------------------------------------------------------------

type Person = {
  userId: string;
  name: string;
  role: string;
  isMe: boolean;
  calendar: { status: "connected" | "expired" | "not_connected"; since: string | null; sample: boolean };
  gmail: { status: "on" | "off" | "problem"; lastCopiedAt: string | null; problem: string | null };
};
type StaffData = { configured: boolean; isDemo: boolean; everyone: boolean; people: Person[] };

const ROLE_LABELS: Record<string, string> = { owner: "Owner", admin: "Admin", staff: "Staff", readonly: "Read only" };

function CalendarStatus({ c }: { c: Person["calendar"] }) {
  if (c.status === "connected") return <Pill tone="green">{c.sample ? "Connected (sample)" : "Connected"}</Pill>;
  if (c.status === "expired") return <Pill tone="red">Connect again</Pill>;
  return <Pill tone="grey">Not connected</Pill>;
}
function GmailStatus({ g, sample }: { g: Person["gmail"]; sample: boolean }) {
  if (g.status === "on") return <Pill tone="green">{sample ? "Copying (sample)" : "Copying"}</Pill>;
  if (g.status === "problem") return <Pill tone="red">Problem</Pill>;
  return <Pill tone="grey">Off</Pill>;
}

/** Firm Settings › Integrations › Staff connections: each person's Google Calendar and Gmail. */
export function StaffConnections({ me }: { me: Me }) {
  const search = new URLSearchParams(useSearch());
  const back = search.get("google") ?? search.get("gmail");
  const { data, isLoading, error } = useQuery<StaffData>({ queryKey: ["/api/firm/integrations/staff"] });
  const go = (url: string) => window.location.assign(url);
  const connect = useMutation({ meta: { inlineError: true }, mutationFn: (what: "calendar" | "gmail") => api.send<{ url: string }>("POST", what === "gmail" ? "/api/google/gmail/connect" : "/api/google/connect", { returnTo: "integrations_staff" }), onSuccess: (r) => go(r.url) });
  const disconnect = useMutation({ meta: { inlineError: true }, mutationFn: (what: "calendar" | "gmail") => api.send("POST", what === "gmail" ? "/api/google/gmail/disconnect" : "/api/google/disconnect"), onSuccess: () => toast({ title: "Disconnected" }) });
  if (isLoading) return <Loading size="box" />;
  if (error || !data) return <Empty>{error ? errorMessage(error) : "Not available."}</Empty>;
  const readonly = me.role === "readonly";
  const banner = back === "connected" ? "Connected." : back === "cancelled" ? "The connection was cancelled at Google." : back === "scopes" ? "Google didn't grant every permission ClientFlow asked for. Connect again and allow each one." : back ? "The connection didn't finish. Try again." : null;
  return (
    <Section
      title="Staff connections"
      description={data.everyone ? "Each person's Google Calendar and Gmail. You can connect or disconnect only your own; everyone else's is shown for reference." : "Your Google Calendar and Gmail. Only you can connect or disconnect them."}
    >
      <div className="space-y-4 text-sm">
        {banner && <p className="rounded-md border border-border bg-muted/50 px-3 py-2">{banner}</p>}
        {!data.configured && !data.isDemo && <p className="text-muted-foreground">Google isn't set up on this server yet (GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET), so calendars and Gmail can't be connected.</p>}
        <TableScroll label="Staff connections table" className="-mx-5">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="text-left text-xs text-muted-foreground">
              <tr className="border-b border-border">
                <th className="px-5 py-2 font-medium">Person</th>
                <th className="py-2 pr-4 font-medium">Google Calendar</th>
                <th className="py-2 pr-4 font-medium">Gmail into Messages</th>
                <th className="py-2 pr-5 text-right font-medium">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.people.map((p) => (
                <tr key={p.userId} className="align-top">
                  <td className="px-5 py-3">
                    <p className="font-medium">
                      {p.name}
                      {p.isMe && <span className="font-normal text-muted-foreground"> (you)</span>}
                    </p>
                    <p className="text-xs text-muted-foreground">{ROLE_LABELS[p.role] ?? p.role}</p>
                  </td>
                  <td className="py-3 pr-4">
                    <CalendarStatus c={p.calendar} />
                    {p.calendar.since && <p className="mt-1 text-xs text-muted-foreground">Since {formatDate(p.calendar.since)}</p>}
                  </td>
                  <td className="py-3 pr-4">
                    <GmailStatus g={p.gmail} sample={p.calendar.sample} />
                    {p.gmail.lastCopiedAt && <p className="mt-1 text-xs text-muted-foreground">Last checked {formatDateTime(p.gmail.lastCopiedAt)}</p>}
                    {p.gmail.problem && <p className="mt-1 max-w-xs text-xs text-muted-foreground">{p.gmail.problem}</p>}
                  </td>
                  <td className="py-3 pr-5 text-right">
                    {p.isMe && !readonly && (data.configured || data.isDemo) ? (
                      <div className="flex flex-wrap justify-end gap-2">
                        {p.calendar.status === "connected" ? (
                          <Button size="sm" variant="ghost" disabled={disconnect.isPending} onClick={() => void ask({ title: "Disconnect your Google Calendar?", description: "Your busy times stop being kept off the booking page, and ClientFlow's access is also taken back at Google (which stops Gmail copying too).", confirmLabel: "Disconnect", destructive: true }).then((ok) => ok && disconnect.mutate("calendar"))}>
                            Disconnect calendar
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" disabled={connect.isPending || !data.configured} onClick={() => connect.mutate("calendar")}>
                            {p.calendar.status === "expired" ? "Reconnect calendar" : "Connect calendar"}
                          </Button>
                        )}
                        {p.gmail.status === "off" ? (
                          <Button size="sm" variant="outline" disabled={connect.isPending || !data.configured || data.isDemo} onClick={() => connect.mutate("gmail")}>
                            Connect Gmail
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost" disabled={disconnect.isPending} onClick={() => void ask({ title: "Stop copying your email?", description: "Emails already copied stay on the clients' records.", confirmLabel: "Stop copying", destructive: true }).then((ok) => ok && disconnect.mutate("gmail"))}>
                            Stop copying email
                          </Button>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">{p.isMe ? (readonly ? "Read-only members don't connect accounts" : "") : "Their own to change"}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
        {(connect.error || disconnect.error) && <p className="text-destructive">{errorMessage(connect.error ?? disconnect.error)}</p>}
        <p className="text-xs text-muted-foreground">ClientFlow reads only free and busy times from a calendar, and copies only emails with clients from Gmail; it never sends, changes or deletes mail.{data.isDemo && " In the demo firm, calendars are samples and Gmail can't be connected."}</p>
      </div>
    </Section>
  );
}

// --- 3. Client QuickBooks --------------------------------------------------------------------------

type ClientRow = { id: string; name: string; status: "connected" | "expired" | "not_connected"; services: string[]; companyName: string | null; lastSyncedAt: string | null };
type ClientsData = { available: boolean; isDemo: boolean; canConnect: boolean; counts: Record<ClientRow["status"], number>; total: number; items: ClientRow[] };
const SERVICE_SHORT: Record<string, string> = { bookkeeping: "Accounting", advisory: "CFO" };
const QBO_STATUS: Record<ClientRow["status"], { label: string; tone: keyof typeof TONES }> = {
  connected: { label: "Connected", tone: "green" },
  expired: { label: "Expired", tone: "red" },
  not_connected: { label: "Never connected", tone: "amber" },
};

/** Firm Settings › Integrations › Client QuickBooks: work down the list connecting each client's company. */
export function ClientQuickBooks() {
  const search = new URLSearchParams(useSearch());
  const result = search.get("qbo");
  const [status, setStatus] = useState<"all" | ClientRow["status"]>(() => (["connected", "expired", "not_connected"].includes(search.get("status") ?? "") ? (search.get("status") as ClientRow["status"]) : "all"));
  const [q, setQ] = useState("");
  const pg = useServerPaged(`${status}|${q}`);
  const params = new URLSearchParams({ status, offset: String(pg.offset), limit: String(pg.size), ...(q.trim() ? { q: q.trim() } : {}) });
  const { data, isLoading, error } = useQuery<ClientsData>({ queryKey: [`/api/firm/integrations/clients?${params}`], placeholderData: (p) => p });
  const connect = useMutation({
    meta: { inlineError: true },
    mutationFn: (id: string) => api.send<{ url: string }>("POST", `/api/clients/${id}/qbo/connect`, { returnTo: "integrations_clients" }),
    onSuccess: (r) => window.location.assign(r.url),
  });
  const banner =
    result === "connected" ? "QuickBooks is connected for that client. Carry on down the list." : result === "in_use_firm" ? "That QuickBooks company is already connected as the firm's own books. Choose a different company." : result === "cancelled" ? "The connection was cancelled at Intuit. Nothing changed." : result === "expired" ? "The connection request expired. Try again." : result ? "QuickBooks could not be connected. Try again." : null;
  const c = data?.counts ?? { connected: 0, expired: 0, not_connected: 0 };
  return (
    <div className="space-y-6">
      <Section title="Client QuickBooks" description="Clients with bookkeeping or CFO services and their QuickBooks company. Connect each one in turn; you come back here afterwards.">
        <div className="space-y-4">
          {banner && <p className="rounded-md border border-border bg-muted/50 px-3 py-2 text-sm">{banner}</p>}
          <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm dark:border-blue-900 dark:bg-blue-950/40">
            Intuit requires each client's company to be approved on its own: someone who can sign in to that QuickBooks company as an admin allows ClientFlow. If your firm is already the accountant user on a client's QuickBooks, you can approve it yourself, without the client.
          </div>
          {data && !data.available && <p className="text-sm text-muted-foreground">QuickBooks isn't set up on this server yet, so companies can't be connected.</p>}
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <Segmented
              label="QuickBooks status"
              value={status}
              onChange={setStatus}
              options={[
                { value: "all", label: "All", count: c.connected + c.expired + c.not_connected },
                { value: "expired", label: "Expired", count: c.expired },
                { value: "not_connected", label: "Never connected", count: c.not_connected },
                { value: "connected", label: "Connected", count: c.connected },
              ]}
            />
            <ListSearch value={q} onChange={setQ} placeholder="Search clients" />
          </div>
          {isLoading ? (
            <Loading size="list" />
          ) : error || !data ? (
            <Empty>{error ? errorMessage(error) : "Not available."}</Empty>
          ) : data.items.length === 0 ? (
            <Empty>{q || status !== "all" ? "No clients match. Clear the search or choose All." : "No clients with bookkeeping or CFO services yet. Add the service on a client's page and it appears here."}</Empty>
          ) : (
            <>
              <TableScroll label="Client QuickBooks connections table" className="-mx-5">
                <table className="w-full min-w-[720px] text-sm">
                  <thead className="text-left text-xs text-muted-foreground">
                    <tr className="border-b border-border">
                      <th className="px-5 py-2 font-medium">Client</th>
                      <th className="py-2 pr-4 font-medium">Services</th>
                      <th className="py-2 pr-4 font-medium">QuickBooks</th>
                      <th className="py-2 pr-4 font-medium">Company</th>
                      <th className="py-2 pr-4 text-right font-medium">Last refresh</th>
                      <th className="py-2 pr-5 text-right font-medium">
                        <span className="sr-only">Action</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.items.map((r) => (
                      <tr key={r.id}>
                        <td className="max-w-[16rem] truncate px-5 py-2.5 font-medium">
                          <Link href={`/clients/${r.id}?tab=books`} className="hover:underline">
                            {r.name}
                          </Link>
                        </td>
                        <td className="py-2.5 pr-4 text-muted-foreground">{r.services.map((m) => SERVICE_SHORT[m] ?? m).join(", ")}</td>
                        <td className="py-2.5 pr-4">
                          <Pill tone={QBO_STATUS[r.status].tone}>{QBO_STATUS[r.status].label}</Pill>
                        </td>
                        <td className="max-w-[14rem] truncate py-2.5 pr-4">{r.companyName ?? <span className="text-muted-foreground">—</span>}</td>
                        <td className="tabular py-2.5 pr-4 text-right">{r.lastSyncedAt ? formatDateTime(r.lastSyncedAt) : <span className="text-muted-foreground">—</span>}</td>
                        <td className="py-2.5 pr-5 text-right">
                          {data.canConnect && data.available && r.status !== "connected" && !(data.isDemo && r.status === "not_connected") && (
                            <Button size="sm" variant="outline" disabled={connect.isPending} onClick={() => connect.mutate(r.id)}>
                              {r.status === "expired" ? "Reconnect" : "Connect"}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
              <Pager paged={pg.paged(data.total)} noun="clients" />
            </>
          )}
          {connect.error && <p className="text-sm text-destructive">{errorMessage(connect.error)}</p>}
          {data?.isDemo && <p className="text-xs text-muted-foreground">In the demo firm, the companies are built-in samples: Reconnect restores a sample, nothing goes to Intuit, and a client without a sample company can't be connected.</p>}
        </div>
      </Section>
    </div>
  );
}

// --- 4–6. Leads and the client list ------------------------------------------------------------------

const CHANNELS = ["website", "facebook", "instagram", "linkedin", "google"];

/** Firm Settings › Integrations › Leads and client import: the tracked links, and where clients come from. */
export function LeadsAndImport() {
  const { data } = useQuery<FullSetup>({ queryKey: ["/api/leads/setup"] });
  return (
    <div className="space-y-6">
      <Section title="Lead links" description="Ready-to-copy inquiry links for your website and profiles. Each lead arrives in Clients › Leads tagged with the channel it came from.">
        {data ? <LeadChannelLinks links={data.links} only={CHANNELS} labels={{ google: "Google Business Profile" }} /> : <Loading size="box" />}
      </Section>
      <p className="text-sm text-muted-foreground">
        The website code, the email signature link and Thumbtack are in{" "}
        <Link href="/settings?tab=leads" className="font-medium text-primary hover:underline">
          Firm Settings › Client communication › Leads
        </Link>
        ; step-by-step for each channel is in{" "}
        <a href={`${DOCS_BASE}LEADS.md`} target="_blank" rel="noreferrer" className="font-medium text-primary hover:underline">
          LEADS.md
        </a>
        .
      </p>
      <div className="grid gap-4 lg:grid-cols-2">
        <InfoCard
          title="Client import"
          pill={<Pill tone="grey">CSV</Pill>}
          action={{ label: "Open Import & export", href: "/settings?tab=data" }}
          body={
            <>
              <p>The client list comes in by CSV: a TaxDome export, an OLT Pro client list or a spreadsheet. Columns are matched by name, and you can change the matching before importing.</p>
              <p>Columns named for Social Security numbers, EINs, dates of birth or bank details are never imported, and a value shaped like a Social Security number or EIN is refused in any column. A Notes column is imported as written, so check the notes before importing.</p>
              <p className="text-muted-foreground">Each client's QuickBooks is connected afterwards, one company at a time, from Client QuickBooks.</p>
            </>
          }
        />
        <InfoCard
          title="OLT Pro"
          pill={<Pill tone="grey">CSV</Pill>}
          action={{ label: "Open Import & export", href: "/settings?tab=data" }}
          body={
            <>
              <p>OLT Pro works with ClientFlow by CSV, both ways: import OLT Pro's client list, and export ClientFlow's clients (client code, name, type, email, phone, services) for OLT Pro.</p>
              <p>For a return, a client's Tax input sheet downloads the package for OLT Pro's W-2 and 1099 import.</p>
              <p className="text-muted-foreground">There is no live connection to OLT Pro: nothing is sent to it automatically.</p>
            </>
          }
        />
      </div>
    </div>
  );
}

function InfoCard({ title, pill, body, action }: { title: string; pill: ReactNode; body: ReactNode; action: { label: string; href: string } }) {
  return (
    <section className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-card-border bg-card">
      <div className="flex items-center justify-between gap-3 border-b border-border bg-secondary/50 px-5 py-3">
        <h2 className="flex min-w-0 items-center gap-2 text-[15px] font-semibold">
          <FileSpreadsheet className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden /> {title}
        </h2>
        {pill}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-5 text-sm">
        {body}
        <div className="mt-auto pt-2">
          <Link href={action.href} className="inline-flex min-h-8 items-center rounded-md border border-border px-3 text-xs font-medium hover:bg-muted">
            {action.label}
          </Link>
        </div>
      </div>
    </section>
  );
}

// --- 7. The firm's own QuickBooks ----------------------------------------------------------------------

type FirmModule = "tax" | "bookkeeping" | "payroll" | "advisory" | "other";
type FirmStatus = {
  available: boolean;
  isDemo: boolean;
  connected: boolean;
  status: "connected" | "needs_reconnect" | null;
  companyName: string | null;
  connectedAt: string | null;
  lastError: string | null;
  settings: { incomeAccounts: Partial<Record<FirmModule, string>>; depositAccountId: string | null; refundAccountId: string | null };
  settingsComplete: boolean;
  enabled: boolean;
  enabledAt: string | null;
  lastSentAt: string | null;
  counts: { sent: number; waiting: number; failed: number; notSent: number };
  canManage: boolean;
};
type Account = { id: string; name: string; type: string };
type LedgerItem = {
  id: string;
  recordType: string;
  label: string;
  client: string;
  invoiceNumber: number | null;
  amountCents: number | null;
  status: "sent" | "waiting" | "failed" | "not_sent";
  quickbooks: string | null;
  attempts: number;
  lastError: string | null;
  nextAttemptAt: string | null;
  sentAt: string | null;
  createdAt: string;
};

const MODULE_ROWS: Array<[FirmModule, string]> = [
  ["other", "Other services (required)"],
  ["tax", "Tax"],
  ["bookkeeping", "Accounting Services"],
  ["payroll", "Payroll"],
  ["advisory", "CFO & Advisory"],
];
const LEDGER_STATUS: Record<LedgerItem["status"], { label: string; tone: keyof typeof TONES }> = {
  sent: { label: "Sent", tone: "green" },
  waiting: { label: "Waiting", tone: "blue" },
  failed: { label: "Failing, retrying", tone: "red" },
  not_sent: { label: "Not sent", tone: "grey" },
};

/** A choice from a list; with more than 15 choices a search box narrows it (type to search). */
function AccountChoice({ label, accounts, value, onChange, optional, disabled }: { label: string; accounts: Account[]; value: string; onChange: (v: string) => void; optional?: string; disabled?: boolean }) {
  const [q, setQ] = useState("");
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    const list = t ? accounts.filter((a) => a.name.toLowerCase().includes(t) || a.id === value) : accounts;
    return list;
  }, [accounts, q, value]);
  return (
    <label className="grid min-w-0 gap-1 text-sm">
      <span className="font-medium">{label}</span>
      {accounts.length > 15 && <input type="search" aria-label={`Search ${label}`} placeholder="Type to search accounts" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm" disabled={disabled} />}
      <select className="h-9 w-full min-w-0 rounded-md border border-input bg-background px-2 text-sm" value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} aria-label={label}>
        <option value="">{optional ?? "Choose an account"}</option>
        {shown.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function AccountsForm({ s }: { s: FirmStatus }) {
  const { data: acc, isLoading, error } = useQuery<{ income: Account[]; deposit: Account[]; refund: Account[] }>({ queryKey: ["/api/firm/integrations/firm-qbo/accounts"], enabled: s.canManage && s.connected });
  const [v, setV] = useState(() => ({ income: { ...s.settings.incomeAccounts }, deposit: s.settings.depositAccountId ?? "", refund: s.settings.refundAccountId ?? "", enabled: s.enabled }));
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("PUT", "/api/firm/integrations/firm-qbo/settings", { incomeAccounts: Object.fromEntries(Object.entries(v.income).filter(([, x]) => !!x)), depositAccountId: v.deposit, refundAccountId: v.refund, enabled: v.enabled }),
    onSuccess: () => toast({ title: v.enabled ? (s.enabled ? "Accounts saved" : "Sending to QuickBooks is on") : "Saved; sending is off" }),
  });
  if (!s.canManage) return <p className="text-sm text-muted-foreground">The owner chooses the accounts and turns sending on.</p>;
  if (isLoading) return <Loading size="box" />;
  if (error || !acc) return <Empty>{error ? errorMessage(error) : "The company's accounts couldn't be read."}</Empty>;
  const ready = !!(v.income.other && v.deposit && v.refund);
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <p className="text-sm text-muted-foreground">Choose once. Each service's fees go to its income account; a service left on “Same as other services” uses the Other services account.</p>
      <div className="grid gap-4 sm:grid-cols-2">
        {MODULE_ROWS.map(([m, label]) => (
          <AccountChoice key={m} label={`Income: ${label}`} accounts={acc.income} value={v.income[m] ?? ""} optional={m === "other" ? undefined : "Same as other services"} onChange={(x) => setV({ ...v, income: { ...v.income, [m]: x } })} />
        ))}
        <AccountChoice label="Payments are deposited to" accounts={acc.deposit} value={v.deposit} onChange={(x) => setV({ ...v, deposit: x })} />
        <AccountChoice label="Refunds are paid from" accounts={acc.refund} value={v.refund} onChange={(x) => setV({ ...v, refund: x })} />
      </div>
      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" className="mt-0.5" checked={v.enabled} onChange={(e) => setV({ ...v, enabled: e.target.checked })} />
        <span>
          <span className="font-medium">Send billing records to QuickBooks</span>
          <span className="block text-muted-foreground">Only records made after this is turned on are sent. Earlier invoices and payments are left as they are; record them in QuickBooks by hand if needed.</span>
        </span>
      </label>
      {save.error && <p className="text-sm text-destructive">{errorMessage(save.error)}</p>}
      <Button type="submit" disabled={save.isPending || (v.enabled && !ready)}>
        {v.enabled && !s.enabled ? "Save and turn on" : "Save"}
      </Button>
    </form>
  );
}

const LEDGER_COLS = "lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_6.5rem_minmax(0,2fr)_7.5rem_6.5rem]";

function Ledger({ canMarkDone }: { canMarkDone: boolean }) {
  const [status, setStatus] = useState<"all" | LedgerItem["status"]>("all");
  const pg = useServerPaged(status);
  const { data, isLoading } = useQuery<{ total: number; items: LedgerItem[] }>({ queryKey: [`/api/firm/integrations/firm-qbo/ledger?status=${status}&offset=${pg.offset}&limit=${pg.size}`], placeholderData: (p) => p });
  const retry = useMutation({ meta: { inlineError: true }, mutationFn: (id: string) => api.send<{ status: string; lastError: string | null }>("POST", `/api/firm/integrations/firm-qbo/ledger/${id}/retry`), onSuccess: (r) => toast({ title: r.status === "sent" ? "Sent to QuickBooks" : "Not sent yet", description: r.lastError ?? undefined }) });
  const done = useMutation({ meta: { inlineError: true }, mutationFn: (id: string) => api.send("POST", `/api/firm/integrations/firm-qbo/ledger/${id}/done`), onSuccess: () => toast({ title: "Marked as recorded by hand" }) });
  return (
    <div className="space-y-3">
      <Segmented
        label="Sync status"
        value={status}
        onChange={setStatus}
        options={[
          { value: "all", label: "All" },
          { value: "failed", label: "Failing" },
          { value: "waiting", label: "Waiting" },
          { value: "sent", label: "Sent" },
          { value: "not_sent", label: "Not sent" },
        ]}
      />
      {isLoading || !data ? (
        <Loading size="box" />
      ) : data.items.length === 0 ? (
        <Empty>{status === "all" ? "Nothing sent yet. Invoices, payments, refunds and adjustments appear here once they are made, after sending is turned on." : "Nothing here."}</Empty>
      ) : (
        <>
          {/* A table on wider screens; on a phone each record stacks, like the Backups page's failed files. */}
          <div className="-mx-5 divide-y divide-border border-y border-border text-sm">
            <div className={cn("hidden gap-3 bg-muted/40 px-5 py-2 text-xs font-medium text-muted-foreground lg:grid", LEDGER_COLS)}>
              <span>Record</span>
              <span>Client</span>
              <span className="text-right">Amount</span>
              <span>Status</span>
              <span>In QuickBooks</span>
              <span className="text-right">Made</span>
            </div>
            {data.items.map((r) => (
              <div key={r.id} className={cn("grid gap-1 px-5 py-2.5 lg:items-start lg:gap-3", LEDGER_COLS)}>
                <div className="flex min-w-0 items-baseline justify-between gap-3 lg:block">
                  <p className="font-medium">
                    {r.label} <span className="whitespace-nowrap text-xs font-normal text-muted-foreground lg:block">Invoice #{r.invoiceNumber ?? "—"}</span>
                  </p>
                  <span className="tabular shrink-0 lg:hidden">{r.amountCents === null ? "—" : formatCents(r.amountCents)}</span>
                </div>
                <span className="min-w-0 truncate">{r.client}</span>
                <span className="tabular hidden text-right lg:block">{r.amountCents === null ? "—" : formatCents(r.amountCents)}</span>
                <div className="min-w-0">
                  <Pill tone={LEDGER_STATUS[r.status].tone}>{LEDGER_STATUS[r.status].label}</Pill>
                  {r.lastError && r.status !== "sent" && <p className="mt-1 text-xs text-muted-foreground">{r.lastError}</p>}
                  {r.status === "failed" && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Tried {plural(r.attempts, "time")}
                      {r.nextAttemptAt ? (Date.parse(r.nextAttemptAt) <= Date.now() ? "; tried again within 5 minutes" : `; next try ${formatDateTime(r.nextAttemptAt)}`) : ""}.
                    </p>
                  )}
                  {(r.status === "failed" || r.status === "waiting") && (
                    <div className="mt-1.5 flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" disabled={retry.isPending} onClick={() => retry.mutate(r.id)}>
                        Retry now
                      </Button>
                      {canMarkDone && (
                        <Button size="sm" variant="ghost" disabled={done.isPending} onClick={() => void ask({ title: "Mark as recorded in QuickBooks by hand?", description: "ClientFlow stops trying to send it. Use this only after recording it in QuickBooks yourself, or it will be missing there.", confirmLabel: "Mark as done", destructive: true }).then((ok) => ok && done.mutate(r.id))}>
                          Mark as done
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                <span className="text-xs text-muted-foreground lg:text-sm lg:text-foreground">
                  <span className="lg:hidden">In QuickBooks: </span>
                  {r.quickbooks ? r.quickbooks.replace(/^\w/, (x) => x.toUpperCase()) : "—"}
                  <span className="lg:hidden"> · Made {formatDate(r.createdAt)}</span>
                </span>
                <span className="tabular hidden whitespace-nowrap text-right lg:block">{formatDate(r.createdAt)}</span>
              </div>
            ))}
          </div>
          <Pager paged={pg.paged(data.total)} noun="records" />
        </>
      )}
      {(retry.error || done.error) && <p className="text-sm text-destructive">{errorMessage(retry.error ?? done.error)}</p>}
    </div>
  );
}

/** Firm Settings › Integrations › The firm's QuickBooks (owners and admins; the owner manages it). */
export function FirmQuickBooks() {
  const search = new URLSearchParams(useSearch());
  const result = search.get("qbo");
  const { data: s, isLoading, error } = useQuery<FirmStatus>({ queryKey: ["/api/firm/integrations/firm-qbo"] });
  const connect = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send<{ url: string }>("POST", "/api/firm/integrations/firm-qbo/connect"), onSuccess: (r) => window.location.assign(r.url) });
  const disconnect = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send("POST", "/api/firm/integrations/firm-qbo/disconnect"), onSuccess: () => toast({ title: "The firm's QuickBooks is disconnected" }) });
  if (isLoading) return <Loading size="box" />;
  if (error || !s) return <Empty>{error ? errorMessage(error) : "Not available."}</Empty>;
  const banner = result === "connected" ? "The firm's QuickBooks is connected. Choose the accounts below, then turn sending on." : result === "cancelled" ? "The connection was cancelled at Intuit." : result === "owner_only" ? "Only the firm's owner can connect the firm's own QuickBooks." : result === "in_use_client" ? "That QuickBooks company is already connected as a client's books. Choose a different company." : result ? "QuickBooks could not be connected. Try again." : null;
  return (
    <div className="space-y-6">
      <Section
        title="The firm's own QuickBooks"
        description="Your firm's own books, not a client's. When sending is on, each invoice, payment, refund, credit and adjustment made in ClientFlow billing is sent to it, once."
        action={
          s.connected || s.status === "needs_reconnect" ? (
            <Pill tone={s.connected && s.enabled ? "green" : s.status === "needs_reconnect" ? "red" : "amber"}>{s.status === "needs_reconnect" ? "Reconnect" : s.enabled ? "Sending on" : "Sending off"}</Pill>
          ) : (
            <Pill tone="grey">Not connected</Pill>
          )
        }
      >
        <div className="space-y-4 text-sm">
          {banner && <p className="rounded-md border border-border bg-muted/50 px-3 py-2">{banner}</p>}
          {!s.available ? (
            <p className="text-muted-foreground">QuickBooks isn't set up on this server yet (the Intuit keys in Replit Secrets), so it can't be connected.</p>
          ) : !s.connected && s.status !== "needs_reconnect" ? (
            <div className="space-y-3">
              <p>Optional. It uses the same Intuit app as client connections: you sign in to your own QuickBooks company and allow ClientFlow. Nothing is sent until you choose the accounts and turn sending on.</p>
              {s.canManage ? (
                <Button onClick={() => connect.mutate()} disabled={connect.isPending}>
                  Connect the firm's QuickBooks
                </Button>
              ) : (
                <p className="text-muted-foreground">Only the firm's owner can connect it.</p>
              )}
            </div>
          ) : (
            <>
              <dl className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
                <Row label="Company" value={s.companyName ?? "QuickBooks company"} />
                <Row label="Connected" value={s.connectedAt ? formatDate(s.connectedAt) : "—"} />
                <Row label="Sending since" value={s.enabledAt ? formatDateTime(s.enabledAt) : "Off"} />
                <Row label="Last sent" value={s.lastSentAt ? formatDateTime(s.lastSentAt) : "Nothing yet"} />
              </dl>
              {s.status === "needs_reconnect" && (
                <div role="alert" className="rounded-md border border-red-300 bg-red-50 px-3 py-2 dark:border-red-900 dark:bg-red-950/40">
                  {s.lastError ?? "QuickBooks access expired."} Records wait and are sent once it is reconnected.
                  {s.canManage && (
                    <Button size="sm" className="ml-2" onClick={() => connect.mutate()}>
                      Reconnect
                    </Button>
                  )}
                </div>
              )}
              {s.isDemo && <p className="text-xs text-muted-foreground">Demo firm: this is a built-in sample company, so nothing reaches Intuit.</p>}
            </>
          )}
          {(connect.error || disconnect.error) && <p className="text-destructive">{errorMessage(connect.error ?? disconnect.error)}</p>}
        </div>
      </Section>

      {(s.connected || s.status === "needs_reconnect") && (
        <>
          <StatRow
            stats={[
              { label: "Sent", value: s.counts.sent, tone: s.counts.sent ? "success" : "default" },
              { label: "Waiting", value: s.counts.waiting },
              { label: "Failing, being retried", value: s.counts.failed, tone: s.counts.failed ? "danger" : "default" },
              { label: "Not sent", value: s.counts.notSent },
            ]}
          />
          <div className="grid gap-6 lg:grid-cols-2">
            <Section title="Accounts" description="Where each record lands in the firm's QuickBooks.">
              <AccountsForm key={`${s.enabled}-${s.settings.depositAccountId}`} s={s} />
            </Section>
            <Section title="What is sent" description="Each ClientFlow record becomes one QuickBooks record, and never a second one.">
              <dl className="grid gap-y-2 text-sm">
                <Row label="Invoice" value="Invoice (numbered CF-1001 and so on), for the client as a customer" />
                <Row label="Payment" value="Payment on that invoice, into the deposit account" />
                <Row label="Refund" value="Refund receipt, paid from the refund account" />
                <Row label="Adjustment, write-off or void" value="Credit memo, applied to the invoice (less any part a refund receipt already covered)" />
                <Row label="Credit applied" value="The client's credit memo applied to the invoice" />
                <Row label="Credit held" value="Nothing on its own: it changes no income (the void's credit memo carries it)" />
              </dl>
              <p className="mt-3 text-xs text-muted-foreground">Customers are matched by the client's name, or added. A record that fails is tried again later (after 5 minutes, then less often, at most daily) and shown below until it goes through.</p>
              <p className="mt-2 text-xs text-muted-foreground">
                A few cases are left to record in QuickBooks by hand: credit held on a client's account, a second payment on a refunded invoice, and a card refund that fails after it was sent.{" "}
                <a href={`${DOCS_BASE}DEPLOY.md#firm-quickbooks`} target="_blank" rel="noreferrer" className="font-medium text-primary hover:underline">
                  What to do in each case
                </a>
              </p>
            </Section>
          </div>
          <Section title="Sent to QuickBooks" description="Every record since sending was turned on, newest first.">
            <Ledger canMarkDone={s.canManage} />
          </Section>
          {s.canManage && (
            <div className="flex justify-end">
              <Button variant="ghost" className="text-destructive" disabled={disconnect.isPending} onClick={() => void ask({ title: "Disconnect the firm's QuickBooks?", description: "Sending stops, and ClientFlow's access is taken back at Intuit. What was already sent stays in QuickBooks.", confirmLabel: "Disconnect", destructive: true }).then((ok) => ok && disconnect.mutate())}>
                Disconnect the firm's QuickBooks
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid min-w-0 gap-0.5 sm:grid-cols-[11rem_minmax(0,1fr)] sm:gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{value}</dd>
    </div>
  );
}

/** Whether this person may see each Integrations tab (the rules live with Firm Settings' other tabs). */
export { integrationTabs } from "@/lib/settings-tabs";
